import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from "@angular/core";
import { BillingCycle, Customer, SubscriptionItem, SubscriptionStatus } from "../api/proxies";
import { ButtonWrapperComponent } from "@vui";
import { TranslocoCoreModule } from "@i18n";
import { MatIconModule } from "@angular/material/icon";
import { DetailsStore } from "./store/details-store";
import { SubscriptionBilling, SubscriptionStatusEnum } from "./store/details.model";
import { SubscriptionDetailsService } from "./store/details-service";
import { finalize } from "rxjs";
import { ProgressSpinnerComponent } from "libs/vui/src/lib/progress-spinner/progress-spinner.component";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { SignUpPlanDialogComponent } from "./sign-up-plan-dialog/sign-up-plan-dialog.component";
import { UpdatePaymentMethodDialogComponent } from "./update-payment-method-dialog/update-payment-method-dialog.component";

@Component({
    selector: "v2-subscription-details",
    templateUrl: "./v2-details.component.html",
    styleUrls: ["./v2-details.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        MatIconModule,
        MatButtonModule,
        MatDialogModule,
        DatePipe,

        TranslocoCoreModule,

        ButtonWrapperComponent,
        ProgressSpinnerComponent,
    ],
    providers: [SubscriptionDetailsService, DetailsStore],
})
export class V2SubscriptionDetailsComponent implements OnInit, OnDestroy {
    private _cdr = inject(ChangeDetectorRef);

    private _store = inject(DetailsStore);

    constructor(private _matDialog: MatDialog) {}

    customer: Customer;

    editionName: string | undefined;

    price: number;

    currentPrice: number;

    billingCycle: BillingCycle;

    startDate: Date;

    items: SubscriptionItem[] | undefined;

    isEditingCreditCard: boolean;

    status: SubscriptionStatus;

    due: SubscriptionBilling[] | undefined;

    future: SubscriptionBilling[] | undefined;

    loading: boolean = false;

    ngOnInit(): void {
        this.loadDetails();
    }

    ngOnDestroy(): void { }

    get hasSubscriptionDetails(): boolean {
        return !!this.editionName;
    }

    get isSubscriptionActive(): boolean {
        return this.status?.code && Number(this.status?.code) === SubscriptionStatusEnum.Active;
    }

    get isTrialing(): boolean {
        return this.status?.code && Number(this.status?.code) === SubscriptionStatusEnum.Trialing;
    }

    get isSubscriptionInactive(): boolean {
        return (
            Number(this.status?.code) === SubscriptionStatusEnum.PastDue ||
            Number(this.status?.code) === SubscriptionStatusEnum.Canceled
        );
    }

    changeSubscription(): void {
        this._matDialog.open(SignUpPlanDialogComponent, {
            width: "80vw",
            maxWidth: "90vw",
            data: {},
        });
    }

    cancelSubscription(): void { }

    createSubscription(): void {
        this._matDialog.open(SignUpPlanDialogComponent, {
            width: "80vw",
            maxWidth: "90vw",
            data: {},
        });
    }
    
    editPaymentMethod(): void {
        this._matDialog.open(UpdatePaymentMethodDialogComponent, {
            width: "80vw",
            maxWidth: "90vw",
            data: {},
        })
        .afterClosed()
        .subscribe((result) => {
            if (result?.success) {
                this.loadDetails();
            }
        });
    }

    private loadDetails(): void {
        this.loading = true;
        this._cdr.markForCheck();
        this._store
            .loadDetails()
            .pipe(
                finalize(() => {
                    this.loading = false;
                    this._cdr.markForCheck();
                })
            )
            .subscribe(() => {
                if (this._store.editionName) {
                    this.editionName = this._store.editionName();
                    this.customer = this._store.customer();
                    this.price = this._store.price();
                    this.currentPrice = this._store.currentPrice();
                    this.billingCycle = this._store.billingCycle();
                    this.startDate = this._store.startDate();
                    this.items = this._store.items();
                    this.status = this._store.status();
                    this.due = this._store.due();
                    this.future = this._store.future();
                }
            });
    }

    registerPaymentMethod(): void { }
}
